import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    Button,
    Chip,
    Paper,
    Divider,
    Grid,
    IconButton,
    Fade,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Collapse,
    Alert,
    Zoom,
    Badge,
    Tooltip,
    useTheme,
    useMediaQuery,
    Skeleton,
    Tab,
    Tabs,
    LinearProgress,
    Avatar,
    Rating,
    Slide,
    Stack,
    Grow,
    Snackbar,
} from '@mui/material';
import {
    Close,
    ShoppingCart,
    CheckCircle,
    Star,
    AccessTime,
    LocalShipping,
    Assignment,
    School,
    MenuBook,
    ExpandMore,
    ExpandLess,
    LocalOffer,
    Security,
    Update,
    Support,
    CheckCircleOutline,
    RadioButtonUnchecked,
    Info,
    Compare,
    TrendingUp,
    Groups,
    EmojiEvents,
    Favorite,
    FavoriteBorder,
    Share,
    ZoomIn,
    ArrowForward,
    ArrowBack,
    PlayCircle,
    Timer,
    AttachMoney,
    Savings,
    AutoAwesome,
    Bolt,
    Done,
    Clear,
    HeadsetMic,
    VerifiedUser,
    CreditCard,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { useCart } from './CartContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import confetti from 'canvas-confetti';

// Animations
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const slideIn = keyframes`
  from { transform: translateX(-100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const ProductDetailsEnhanced = ({ open, onClose, product }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { addToCart, cart } = useCart();
    const navigate = useNavigate();
    
    // All useState hooks
    const [selectedOption, setSelectedOption] = useState(null);
    const [expandedOption, setExpandedOption] = useState(null);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [isFavorite, setIsFavorite] = useState(false);
    const [showComparison, setShowComparison] = useState(false);
    const [countdown, setCountdown] = useState(null);
    const [imageZoom, setImageZoom] = useState(false);
    const [testimonialIndex, setTestimonialIndex] = useState(0);
    const [hoveredOption, setHoveredOption] = useState(null);
    const [shareSnackbar, setShareSnackbar] = useState(false);
    const [cartDialog, setCartDialog] = useState(false);
    const [pauseTestimonials, setPauseTestimonials] = useState(false);

    // Refs
    const optionsRef = useRef(null);
    const comparisonRef = useRef(null);

    // Constants
    const testimonials = [
        {
            name: "Michael Thompson",
            role: "General Contractor",
            rating: 5,
            text: "Passed on my first try! The practice exams were exactly what I needed.",
            avatar: "MT"
        },
        {
            name: "Sarah Martinez",
            role: "Electrical Contractor",
            rating: 5,
            text: "The study materials were comprehensive and well-organized. Worth every penny!",
            avatar: "SM"
        },
        {
            name: "James Wilson",
            role: "Plumbing Contractor",
            rating: 5,
            text: "Best investment I made for my career. The physical books were a game-changer.",
            avatar: "JW"
        },
        {
            name: "Robert Davis",
            role: "HVAC Contractor",
            rating: 5,
            text: "The online platform made studying so convenient. I could practice anywhere!",
            avatar: "RD"
        },
        {
            name: "Jennifer Chen",
            role: "Roofing Contractor",
            rating: 5,
            text: "Questions were spot-on with what appeared on the actual exam. Highly recommend!",
            avatar: "JC"
        },
        {
            name: "David Rodriguez",
            role: "General Contractor",
            rating: 5,
            text: "The rental option saved me so much money. Books arrived quickly and in perfect condition.",
            avatar: "DR"
        },
        {
            name: "Lisa Anderson",
            role: "Electrical Contractor",
            rating: 5,
            text: "Customer support was amazing. They answered all my questions promptly.",
            avatar: "LA"
        },
        {
            name: "Thomas Brown",
            role: "Plumbing Contractor",
            rating: 5,
            text: "I failed twice before finding this. Passed with flying colors using their materials!",
            avatar: "TB"
        },
        {
            name: "Maria Garcia",
            role: "General Contractor",
            rating: 5,
            text: "The practice tests really helped build my confidence. Worth every dollar.",
            avatar: "MG"
        },
        {
            name: "Kevin Johnson",
            role: "HVAC Contractor",
            rating: 5,
            text: "Six months of access was perfect. I had plenty of time to prepare at my own pace.",
            avatar: "KJ"
        },
        {
            name: "Amanda White",
            role: "Landscape Contractor",
            rating: 5,
            text: "The books covered everything I needed. Couldn't have passed without them!",
            avatar: "AW"
        },
        {
            name: "Christopher Lee",
            role: "Electrical Contractor",
            rating: 5,
            text: "Best study materials I've found. Clear explanations and relevant content.",
            avatar: "CL"
        }
    ];

    // All useEffect hooks
    useEffect(() => {
        if (product && product.purchaseOptions && product.purchaseOptions.length > 0) {
            const defaultOption = product.purchaseOptions.find(opt => opt.optionType === 'studyProgramOnly');
            setSelectedOption(defaultOption || product.purchaseOptions[0]);
        }
    }, [product]);

    useEffect(() => {
        if (open) {
            const endTime = new Date().getTime() + 24 * 60 * 60 * 1000;
            const interval = setInterval(() => {
                const now = new Date().getTime();
                const distance = endTime - now;
                
                if (distance < 0) {
                    clearInterval(interval);
                    setCountdown(null);
                } else {
                    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
                    const seconds = Math.floor((distance % (1000 * 60)) / 1000);
                    setCountdown({ hours, minutes, seconds });
                }
            }, 1000);
            
            return () => clearInterval(interval);
        }
    }, [open]);

    useEffect(() => {
        if (!pauseTestimonials) {
            const interval = setInterval(() => {
                setTestimonialIndex((prev) => (prev + 1) % testimonials.length);
            }, 8000); // Rotate every 8 seconds for better UX
            return () => clearInterval(interval);
        }
    }, [testimonials.length, pauseTestimonials]);

    // Early return after all hooks
    if (!product) return null;

    // Handler functions
    const handleOptionSelect = (option) => {
        setSelectedOption(option);
        setExpandedOption(null);
        
        if (navigator.vibrate) {
            navigator.vibrate(50);
        }
        
        // Automatically add to cart when option is selected
        const productWithOption = {
            ...product,
            selectedOption: option.optionType,
            price: option.price,
            displayName: option.displayName,
            isTaxable: option.isTaxable,
            quantity: 1
        };
        
        addToCart(productWithOption);
        
        // Show confetti
        confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#ff5e1c', '#4caf50', '#2196f3', '#9c27b0']
        });
        
        setCartDialog(true);
    };

    const handleAddToCart = () => {
        if (!selectedOption) return;
        
        const productWithOption = {
            ...product,
            selectedOption: selectedOption.optionType,
            price: selectedOption.price,
            displayName: selectedOption.displayName,
            isTaxable: selectedOption.isTaxable,
            quantity: 1
        };
        
        addToCart(productWithOption);
        
        confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#ff5e1c', '#4caf50', '#2196f3', '#9c27b0']
        });
        
        setShowSuccessAlert(true);
        setTimeout(() => {
            setShowSuccessAlert(false);
            onClose();
        }, 2000);
    };

    const handleShare = async () => {
        const shareData = {
            title: product.name,
            text: `Check out this contractor exam prep: ${product.name}`,
            url: window.location.href
        };

        try {
            if (navigator.share && isMobile) {
                await navigator.share(shareData);
            } else {
                await navigator.clipboard.writeText(window.location.href);
                setShareSnackbar(true);
            }
        } catch (err) {
            console.error('Error sharing:', err);
            setShareSnackbar(true);
        }
    };

    const getOptionIcon = (optionType) => {
        switch (optionType) {
            case 'studyProgramOnly':
                return <School />;
            case 'studyProgramWithBooks':
                return <AutoAwesome />;
            case 'booksOnly':
                return <MenuBook />;
            default:
                return <Info />;
        }
    };

    const getOptionColor = (optionType) => {
        switch (optionType) {
            case 'studyProgramOnly':
                return '#2196f3';
            case 'studyProgramWithBooks':
                return '#ff5722';
            case 'booksOnly':
                return '#9c27b0';
            default:
                return '#757575';
        }
    };

    const calculateSavings = (optionType) => {
        if (optionType !== 'studyProgramWithBooks') {
            return 0;
        }
        
        // Find the prices of study program only and books only options
        const studyOnlyOption = product.purchaseOptions?.find(opt => opt.optionType === 'studyProgramOnly');
        const booksOnlyOption = product.purchaseOptions?.find(opt => opt.optionType === 'booksOnly');
        const completePackageOption = product.purchaseOptions?.find(opt => opt.optionType === 'studyProgramWithBooks');
        
        if (studyOnlyOption && booksOnlyOption && completePackageOption) {
            // Calculate savings: (Digital Access + Books Only) - Complete Package
            const separatePricesTotal = studyOnlyOption.price + booksOnlyOption.price;
            const savings = separatePricesTotal - completePackageOption.price;
            return Math.max(0, Math.round(savings)); // Ensure non-negative and round to nearest dollar
        }
        
        return 0;
    };

    const features = [
        { icon: <Update />, text: 'Regularly updated content', color: '#2196f3' },
        { icon: <VerifiedUser />, text: 'Expert-verified questions', color: '#4caf50' },
        { icon: <AccessTime />, text: '6-month online access', color: '#ff9800' },
        { icon: <HeadsetMic />, text: 'Live Support', color: '#9c27b0' },
    ];

    const comparisonData = [
        { feature: "Online Practice Exams", studyOnly: true, booksOnly: false, withBooks: true, rentBooks: true },
        { feature: "6-Month Access", studyOnly: true, booksOnly: false, withBooks: true, rentBooks: true },
        { feature: "Physical Study Books", studyOnly: false, booksOnly: true, withBooks: true, rentBooks: true },
        { feature: "Keep Books Forever", studyOnly: false, booksOnly: true, withBooks: true, rentBooks: false },
        { feature: "Updates Included", studyOnly: true, booksOnly: false, withBooks: true, rentBooks: true },
        { feature: "Money Back Guarantee", studyOnly: false, booksOnly: false, withBooks: true, rentBooks: false },
    ];

    const TabPanel = ({ children, value, index }) => (
        <div hidden={value !== index}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                fullScreen={isMobile}
                TransitionComponent={Fade}
                transitionDuration={300}
                scroll="paper"
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : 3,
                        overflow: 'auto',
                        maxHeight: isMobile ? '100vh' : '90vh',
                    }
                }}
            >
                <Box sx={{ position: 'relative' }}>
                    {/* Header with gradient and properly positioned icons */}
                    <Box
                        sx={{
                            background: 'linear-gradient(135deg, #ff5e1c 0%, #ff8c00 100%)',
                            color: 'white',
                            p: 3,
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Decorative background pattern */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                opacity: 0.1,
                                background: `repeating-linear-gradient(
                                    45deg,
                                    transparent,
                                    transparent 10px,
                                    rgba(255,255,255,0.1) 10px,
                                    rgba(255,255,255,0.1) 20px
                                )`,
                            }}
                        />
                        
                        {/* Header content */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center" position="relative">
                            <Typography variant="h5" fontWeight="bold">
                                {product.name}
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <IconButton
                                    onClick={() => setIsFavorite(!isFavorite)}
                                    sx={{ 
                                        color: 'white',
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                                    }}
                                >
                                    {isFavorite ? <Favorite /> : <FavoriteBorder />}
                                </IconButton>
                                <IconButton
                                    onClick={handleShare}
                                    sx={{ 
                                        color: 'white',
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                                    }}
                                >
                                    <Share />
                                </IconButton>
                                <IconButton
                                    onClick={onClose}
                                    sx={{ 
                                        color: 'white',
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                                    }}
                                >
                                    <Close />
                                </IconButton>
                            </Stack>
                        </Stack>

                        {/* Limited time offer banner */}
                        {countdown && (
                            <Box
                                sx={{
                                    mt: 2,
                                    p: 1.5,
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                }}
                            >
                                <Timer />
                                <Typography variant="body2" fontWeight="bold">
                                    Limited Time Offer Ends In: {String(countdown.hours).padStart(2, '0')}:
                                    {String(countdown.minutes).padStart(2, '0')}:
                                    {String(countdown.seconds).padStart(2, '0')}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    <DialogContent sx={{ p: 0, overflow: 'auto' }}>
                        {/* Tabs */}
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs
                                value={activeTab}
                                onChange={(e, newValue) => setActiveTab(newValue)}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{
                                    '& .MuiTab-root': {
                                        textTransform: 'none',
                                        fontWeight: 600,
                                    }
                                }}
                            >
                                <Tab label="Overview" icon={<Info />} iconPosition="start" />
                                <Tab label="Choose Package" icon={<ShoppingCart />} iconPosition="start" />
                                <Tab label="Compare" icon={<Compare />} iconPosition="start" />
                                <Tab label="Reviews" icon={<Star />} iconPosition="start" />
                            </Tabs>
                        </Box>

                        {/* Tab Panels */}
                        <Box sx={{ px: { xs: 2, md: 4 }, pb: { xs: 2, md: 4 } }}>
                            {/* Overview Tab */}
                            <TabPanel value={activeTab} index={0}>
                                <Grid container spacing={4}>
                                    <Grid item xs={12} md={5}>
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                borderRadius: 2,
                                                overflow: 'hidden',
                                                cursor: imageZoom ? 'zoom-out' : 'zoom-in',
                                                '&:hover img': {
                                                    transform: imageZoom ? 'scale(2)' : 'scale(1.05)',
                                                }
                                            }}
                                            onClick={() => setImageZoom(!imageZoom)}
                                        >
                                            {imageLoading && (
                                                <Skeleton
                                                    variant="rectangular"
                                                    width="100%"
                                                    height={400}
                                                    animation="wave"
                                                />
                                            )}
                                            <img
                                                src={product.imageUrl}
                                                alt={product.name}
                                                style={{
                                                    width: '100%',
                                                    height: 'auto',
                                                    display: imageLoading ? 'none' : 'block',
                                                    transition: 'transform 0.3s ease',
                                                    transform: imageZoom ? 'scale(2)' : 'scale(1)',
                                                }}
                                                onLoad={() => setImageLoading(false)}
                                            />
                                            <Tooltip title={imageZoom ? "Click to zoom out" : "Click to zoom in"}>
                                                <IconButton
                                                    sx={{
                                                        position: 'absolute',
                                                        bottom: 8,
                                                        right: 8,
                                                        bgcolor: 'rgba(255,255,255,0.9)',
                                                        '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
                                                    }}
                                                >
                                                    <ZoomIn />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={7}>
                                        <Typography variant="h4" gutterBottom fontWeight="bold">
                                            {product.name}
                                        </Typography>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                            <Rating value={product.rating || 5} readOnly />
                                            <Typography variant="body2" color="text.secondary">
                                                ({product.reviews || '800+'} reviews)
                                            </Typography>
                                            <Chip
                                                label={product.type}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </Box>

                                        <Typography variant="body1" color="text.secondary" paragraph>
                                            {product.description || "Comprehensive study material designed to help you pass your contractor exam with confidence. This resource includes practice questions, detailed explanations, and key concepts to ensure you're fully prepared."}
                                        </Typography>

                                        {/* Trust badges */}
                                        <Grid container spacing={2} sx={{ mt: 3 }}>
                                            {[
                                                { icon: <VerifiedUser />, text: "Verified Content" },
                                                { icon: <Security />, text: "Secure Payment" },
                                                { icon: <HeadsetMic />, text: "Live Support" },
                                                { icon: <EmojiEvents />, text: "No Pass No Pay Guarantee" }
                                            ].map((badge, idx) => (
                                                <Grid item xs={6} sm={3} key={idx}>
                                                    <Paper
                                                        elevation={0}
                                                        sx={{
                                                            p: 2,
                                                            textAlign: 'center',
                                                            bgcolor: 'grey.50',
                                                            borderRadius: 2,
                                                        }}
                                                    >
                                                        <Box sx={{ color: 'primary.main', mb: 1 }}>
                                                            {badge.icon}
                                                        </Box>
                                                        <Typography variant="caption" fontWeight="bold">
                                                            {badge.text}
                                                        </Typography>
                                                    </Paper>
                                                </Grid>
                                            ))}
                                        </Grid>

                                        {/* Quick action buttons */}
                                        <Box sx={{ mt: 4 }}>
                                            <Button
                                                variant="contained"
                                                size="large"
                                                fullWidth
                                                onClick={() => setActiveTab(1)}
                                                endIcon={<ArrowForward />}
                                                sx={{
                                                    py: 2,
                                                    bgcolor: '#ff5e1c',
                                                    '&:hover': { bgcolor: '#e54e0f' }
                                                }}
                                            >
                                                Choose Your Package
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </TabPanel>

                            {/* Choose Package Tab */}
                            <TabPanel value={activeTab} index={1}>
                                <Typography variant="h5" gutterBottom fontWeight="bold" textAlign="center">
                                    Select Your Study Package
                                </Typography>
                                <Typography variant="body1" color="text.secondary" textAlign="center" paragraph>
                                    Choose the option that best fits your learning style and budget
                                </Typography>

                                <Grid container spacing={3} sx={{ mt: 2, justifyContent: 'center' }}>
                                    {product.purchaseOptions && product.purchaseOptions
                                        .filter(option => {
                                            // Filter out rent books option
                                            if (option.optionType === 'studyProgramAndRentBooks') return false;

                                            // Check if product has any books available
                                            const hasBooks = product.purchaseOptions.some(opt =>
                                                opt.books && opt.books.length > 0
                                            );

                                            // If product has no books, only show studyProgramOnly option
                                            if (!hasBooks) {
                                                return option.optionType === 'studyProgramOnly';
                                            }

                                            return true;
                                        })
                                        .sort((a, b) => {
                                            const order = ['studyProgramOnly', 'booksOnly', 'studyProgramWithBooks'];
                                            return order.indexOf(a.optionType) - order.indexOf(b.optionType);
                                        })
                                        .map((option) => {
                                        const isSelected = selectedOption?._id === option._id;
                                        const isHovered = hoveredOption === option._id;
                                        const optionColor = getOptionColor(option.optionType);
                                        const savings = calculateSavings(option.optionType);
                                        const isBestValue = option.optionType === 'studyProgramWithBooks';

                                        return (
                                            <Grid item xs={12} md={4} key={option._id}>
                                                <Card
                                                    onMouseEnter={() => setHoveredOption(option._id)}
                                                    onMouseLeave={() => setHoveredOption(null)}
                                                    sx={{
                                                        height: '100%',
                                                        cursor: 'pointer',
                                                        position: 'relative',
                                                        overflow: 'visible',
                                                        border: isSelected 
                                                            ? `3px solid ${optionColor}` 
                                                            : isBestValue 
                                                            ? '2px solid #ff5e1c'
                                                            : '2px solid transparent',
                                                        bgcolor: isSelected ? `${optionColor}08` : 'background.paper',
                                                        transition: 'all 0.3s ease',
                                                        transform: isHovered ? 'translateY(-12px) scale(1.02)' : 'translateY(0) scale(1)',
                                                        boxShadow: isHovered ? 12 : isSelected ? 8 : 2,
                                                        '&::before': {
                                                            content: '""',
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            height: 6,
                                                            background: optionColor,
                                                            transform: isHovered ? 'scaleX(1)' : 'scaleX(0)',
                                                            transition: 'transform 0.3s ease',
                                                            transformOrigin: 'left',
                                                        }
                                                    }}
                                                    onClick={() => handleOptionSelect(option)}
                                                >
                                                    {isBestValue && (
                                                        <Box sx={{
                                                            position: 'absolute',
                                                            top: -12,
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            zIndex: 2,
                                                            display: 'flex',
                                                            gap: 1
                                                        }}>
                                                            <Chip
                                                                icon={<Star />}
                                                                label="BEST VALUE"
                                                                color="success"
                                                                size="small"
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    animation: `${pulse} 2s infinite`,
                                                                    background: 'linear-gradient(45deg, #ff5e1c 30%, #ff8c00 90%)'
                                                                }}
                                                            />
                                                        </Box>
                                                    )}

                                                    <CardContent sx={{ p: 4 }}>
                                                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                                                            <Box
                                                                sx={{
                                                                    width: 80,
                                                                    height: 80,
                                                                    borderRadius: '50%',
                                                                    bgcolor: isSelected ? optionColor : `${optionColor}15`,
                                                                    color: isSelected ? 'white' : optionColor,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    mx: 'auto',
                                                                    mb: 2,
                                                                    transition: 'all 0.3s ease',
                                                                    animation: isHovered ? `${pulse} 1s infinite` : 'none',
                                                                    boxShadow: isSelected ? `0 0 0 4px ${optionColor}20` : 'none'
                                                                }}
                                                            >
                                                                {React.cloneElement(getOptionIcon(option.optionType), { sx: { fontSize: 40 } })}
                                                            </Box>
                                                            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: isSelected ? optionColor : 'text.primary' }}>
                                                                {(() => {
                                                                    switch (option.optionType) {
                                                                        case 'studyProgramOnly':
                                                                            return 'Digital Access';
                                                                        case 'booksOnly':
                                                                            return 'Books Only';
                                                                        case 'studyProgramWithBooks':
                                                                            return 'Complete Package';
                                                                        default:
                                                                            return option.displayName;
                                                                    }
                                                                })()}
                                                            </Typography>
                                                            <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
                                                                {(() => {
                                                                    switch (option.optionType) {
                                                                        case 'studyProgramOnly':
                                                                            return 'Access to Practice Exams';
                                                                        case 'booksOnly':
                                                                            return 'Physical books only - No online access';
                                                                        case 'studyProgramWithBooks':
                                                                            return 'Everything in Digital + Physical books';
                                                                        default:
                                                                            return '';
                                                                    }
                                                                })()}
                                                            </Typography>
                                                            <Typography variant="h2" fontWeight="bold" color={isSelected ? optionColor : 'text.primary'}>
                                                                ${option.price}
                                                            </Typography>
                                                            {option.isTaxable && (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    + applicable taxes
                                                                </Typography>
                                                            )}
                                                            {savings > 0 && (
                                                                <Chip
                                                                    label={`Save $${savings}`}
                                                                    size="small"
                                                                    color="success"
                                                                    sx={{ mt: 1 }}
                                                                />
                                                            )}
                                                        </Box>

                                                        <Divider sx={{ my: 2 }} />

                                                        <List dense>
                                                            {option.optionType === 'studyProgramOnly' && (
                                                                <>
                                                                    <ListItem>
                                                                        <ListItemIcon>
                                                                            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                                                                        </ListItemIcon>
                                                                        <ListItemText primary="6-month online access" />
                                                                    </ListItem>
                                                                    <ListItem>
                                                                        <ListItemIcon>
                                                                            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                                                                        </ListItemIcon>
                                                                        <ListItemText primary="Practice exams & quizzes" />
                                                                    </ListItem>
                                                                    <ListItem>
                                                                        <ListItemIcon>
                                                                            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                                                                        </ListItemIcon>
                                                                        <ListItemText primary="Instant digital access" />
                                                                    </ListItem>
                                                                    {!product.purchaseOptions?.some(opt => opt.optionType === 'studyProgramWithBooks') && (
                                                                        <ListItem
                                                                            component={RouterLink}
                                                                            to="/guarantee"
                                                                            sx={{
                                                                                textDecoration: 'none',
                                                                                color: 'inherit',
                                                                                cursor: 'pointer',
                                                                                '&:hover': {
                                                                                    bgcolor: 'action.hover',
                                                                                }
                                                                            }}
                                                                        >
                                                                            <ListItemIcon>
                                                                                <VerifiedUser sx={{ color: 'success.main', fontSize: 20 }} />
                                                                            </ListItemIcon>
                                                                            <ListItemText primary="No Pass No Pay Guarantee" />
                                                                        </ListItem>
                                                                    )}
                                                                </>
                                                            )}
                                                            {option.optionType === 'studyProgramWithBooks' && (
                                                                <>
                                                                    <ListItem>
                                                                        <ListItemIcon>
                                                                            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                                                                        </ListItemIcon>
                                                                        <ListItemText primary="Everything in Study Program" />
                                                                    </ListItem>
                                                                    <ListItem>
                                                                        <ListItemIcon>
                                                                            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                                                                        </ListItemIcon>
                                                                        <ListItemText primary="Physical books shipped" />
                                                                    </ListItem>
                                                                    <ListItem>
                                                                        <ListItemIcon>
                                                                            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                                                                        </ListItemIcon>
                                                                        <ListItemText primary="Keep books forever" />
                                                                    </ListItem>
                                                                    <ListItem
                                                                        component={RouterLink}
                                                                        to="/guarantee"
                                                                        sx={{
                                                                            textDecoration: 'none',
                                                                            color: 'inherit',
                                                                            cursor: 'pointer',
                                                                            '&:hover': {
                                                                                bgcolor: 'action.hover',
                                                                            }
                                                                        }}
                                                                    >
                                                                        <ListItemIcon>
                                                                            <VerifiedUser sx={{ color: 'success.main', fontSize: 20 }} />
                                                                        </ListItemIcon>
                                                                        <ListItemText primary="No Pass No Pay Guarantee" />
                                                                    </ListItem>
                                                                </>
                                                            )}
                                                            {option.optionType === 'booksOnly' && (
                                                                <>
                                                                    <ListItem>
                                                                        <ListItemIcon>
                                                                            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                                                                        </ListItemIcon>
                                                                        <ListItemText primary="Physical books only" />
                                                                    </ListItem>
                                                                    <ListItem>
                                                                        <ListItemIcon>
                                                                            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                                                                        </ListItemIcon>
                                                                        <ListItemText primary="Keep books forever" />
                                                                    </ListItem>
                                                                    <ListItem>
                                                                        <ListItemIcon>
                                                                            <Clear sx={{ color: 'error.main', fontSize: 20 }} />
                                                                        </ListItemIcon>
                                                                        <ListItemText primary="No online access" />
                                                                    </ListItem>
                                                                </>
                                                            )}
                                                            {false && (
                                                                <>
                                                                    <ListItem>
                                                                        <ListItemIcon>
                                                                            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                                                                        </ListItemIcon>
                                                                        <ListItemText primary="Everything in Study Program" />
                                                                    </ListItem>
                                                                    <ListItem>
                                                                        <ListItemIcon>
                                                                            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                                                                        </ListItemIcon>
                                                                        <ListItemText primary="Rental books shipped" />
                                                                    </ListItem>
                                                                    <ListItem>
                                                                        <ListItemIcon>
                                                                            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                                                                        </ListItemIcon>
                                                                        <ListItemText primary="Prepaid return label" />
                                                                    </ListItem>
                                                                </>
                                                            )}
                                                        </List>

                                                        {option.books && option.books.length > 0 && (option.optionType === 'studyProgramWithBooks' || option.optionType === 'booksOnly') && (
                                                            <Box sx={{ mt: 2 }}>
                                                                <Button
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setExpandedOption(expandedOption === option._id ? null : option._id);
                                                                    }}
                                                                    endIcon={expandedOption === option._id ? <ExpandLess /> : <ExpandMore />}
                                                                    sx={{ color: optionColor }}
                                                                >
                                                                    {option.books.length} books included
                                                                </Button>
                                                                <Collapse in={expandedOption === option._id}>
                                                                    <List dense sx={{ mt: 1 }}>
                                                                        {option.books.map((book, idx) => (
                                                                            <ListItem key={idx} sx={{ py: 0.5 }}>
                                                                                <ListItemIcon sx={{ minWidth: 30 }}>
                                                                                    <MenuBook sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                                                </ListItemIcon>
                                                                                <ListItemText
                                                                                    primary={book}
                                                                                    primaryTypographyProps={{
                                                                                        variant: 'caption',
                                                                                        color: 'text.secondary',
                                                                                    }}
                                                                                />
                                                                            </ListItem>
                                                                        ))}
                                                                    </List>
                                                                </Collapse>
                                                            </Box>
                                                        )}

                                                        <Box sx={{ mt: 3 }}>
                                                            <Button
                                                                variant={isSelected ? "contained" : "outlined"}
                                                                fullWidth
                                                                sx={{
                                                                    borderColor: optionColor,
                                                                    color: isSelected ? 'white' : optionColor,
                                                                    bgcolor: isSelected ? optionColor : 'transparent',
                                                                    '&:hover': {
                                                                        bgcolor: isSelected ? optionColor : `${optionColor}15`,
                                                                        borderColor: optionColor,
                                                                    }
                                                                }}
                                                            >
                                                                {isSelected ? <Done sx={{ mr: 1 }} /> : <ShoppingCart sx={{ mr: 1 }} />}
                                                                {isSelected ? 'Selected' : 'Select & Add to Cart'}
                                                            </Button>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>

                            </TabPanel>

                            {/* Compare Tab */}
                            <TabPanel value={activeTab} index={2}>
                                <Typography variant="h5" gutterBottom fontWeight="bold" textAlign="center">
                                    Compare All Options
                                </Typography>
                                <Typography variant="body1" color="text.secondary" textAlign="center" paragraph>
                                    See what's included in each package
                                </Typography>

                                <Paper elevation={0} sx={{ mt: 3, overflow: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ 
                                                    padding: '16px', 
                                                    textAlign: 'left', 
                                                    borderBottom: '2px solid #e0e0e0',
                                                    backgroundColor: '#f5f5f5',
                                                    position: 'sticky',
                                                    left: 0,
                                                    zIndex: 1
                                                }}>
                                                    Features
                                                </th>
                                                <th style={{ 
                                                    padding: '16px', 
                                                    textAlign: 'center', 
                                                    borderBottom: '2px solid #e0e0e0',
                                                    backgroundColor: '#f5f5f5',
                                                    color: '#2196f3'
                                                }}>
                                                    Digital Access<br />
                                                    <strong>${product.purchaseOptions?.find(opt => opt.optionType === 'studyProgramOnly')?.price || 475}</strong>
                                                </th>
                                                <th style={{ 
                                                    padding: '16px', 
                                                    textAlign: 'center', 
                                                    borderBottom: '2px solid #e0e0e0',
                                                    backgroundColor: '#f5f5f5',
                                                    color: '#9c27b0'
                                                }}>
                                                    Books Only<br />
                                                    <strong>${product.purchaseOptions?.find(opt => opt.optionType === 'booksOnly')?.price || 1425}</strong>
                                                </th>
                                                <th style={{ 
                                                    padding: '16px', 
                                                    paddingTop: '40px',
                                                    textAlign: 'center', 
                                                    borderBottom: '2px solid #e0e0e0',
                                                    backgroundColor: '#fff3e0',
                                                    color: '#ff5722',
                                                    position: 'relative'
                                                }}>
                                                    <Chip
                                                        label="BEST VALUE"
                                                        size="small"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            bgcolor: '#ff5e1c',
                                                            color: 'white',
                                                            fontSize: '0.7rem',
                                                            fontWeight: 700
                                                        }}
                                                    />
                                                    Complete Package<br />
                                                    <strong>${product.purchaseOptions?.find(opt => opt.optionType === 'studyProgramWithBooks')?.price || 1900}</strong>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {comparisonData
                                                .filter(row => row.feature !== "Keep Books Forever")
                                                .map((row, idx) => (
                                                <tr key={idx} style={{ 
                                                    backgroundColor: idx % 2 === 0 ? '#fafafa' : 'white' 
                                                }}>
                                                    <td style={{ 
                                                        padding: '12px 16px', 
                                                        borderBottom: '1px solid #e0e0e0',
                                                        fontWeight: 500,
                                                        position: 'sticky',
                                                        left: 0,
                                                        backgroundColor: idx % 2 === 0 ? '#fafafa' : 'white',
                                                    }}>
                                                        {row.feature}
                                                    </td>
                                                    <td style={{ 
                                                        padding: '12px 16px', 
                                                        textAlign: 'center', 
                                                        borderBottom: '1px solid #e0e0e0' 
                                                    }}>
                                                        {typeof row.studyOnly === 'boolean' ? (
                                                            row.studyOnly ? (
                                                                <CheckCircle sx={{ color: '#ff5e1c' }} />
                                                            ) : (
                                                                <Clear sx={{ color: 'error.main' }} />
                                                            )
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">
                                                                {row.studyOnly}
                                                            </Typography>
                                                        )}
                                                    </td>
                                                    <td style={{ 
                                                        padding: '12px 16px', 
                                                        textAlign: 'center', 
                                                        borderBottom: '1px solid #e0e0e0' 
                                                    }}>
                                                        {typeof row.booksOnly === 'boolean' ? (
                                                            row.booksOnly ? (
                                                                <CheckCircle sx={{ color: '#ff5e1c' }} />
                                                            ) : (
                                                                <Clear sx={{ color: 'error.main' }} />
                                                            )
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">
                                                                {row.booksOnly}
                                                            </Typography>
                                                        )}
                                                    </td>
                                                    <td style={{ 
                                                        padding: '12px 16px', 
                                                        textAlign: 'center', 
                                                        borderBottom: '1px solid #e0e0e0',
                                                        backgroundColor: idx % 2 === 0 ? '#fff8f5' : '#fffbf8',
                                                    }}>
                                                        {typeof row.withBooks === 'boolean' ? (
                                                            row.withBooks ? (
                                                                <CheckCircle sx={{ color: '#ff5e1c' }} />
                                                            ) : (
                                                                <Clear sx={{ color: 'error.main' }} />
                                                            )
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">
                                                                {row.withBooks}
                                                            </Typography>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Paper>
                            </TabPanel>

                            {/* Reviews Tab */}
                            <TabPanel value={activeTab} index={3}>
                                <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
                                    <Typography variant="h4" gutterBottom fontWeight="bold" textAlign="center" sx={{ mb: 1 }}>
                                        What Our Customers Say
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
                                        Join thousands of successful contractors who passed their exams with our help
                                    </Typography>
                                    
                                    {/* Overall Rating */}
                                    <Paper
                                        elevation={3}
                                        sx={{
                                            textAlign: 'center',
                                            mb: 5,
                                            p: 4,
                                            background: 'linear-gradient(135deg, #ff5e1c 0%, #ff8c42 100%)',
                                            borderRadius: 3,
                                            position: 'relative',
                                            overflow: 'hidden',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                background: 'radial-gradient(circle at top left, rgba(255,255,255,0.2), transparent)',
                                                pointerEvents: 'none'
                                            }
                                        }}
                                    >
                                        <Stack direction="row" justifyContent="center" alignItems="center" spacing={4}>
                                            <Box>
                                                <Typography variant="h1" fontWeight="bold" sx={{ color: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.2)' }}>
                                                    4.8
                                                </Typography>
                                                <Rating 
                                                    value={4.8} 
                                                    readOnly 
                                                    size="large" 
                                                    precision={0.1}
                                                    sx={{ 
                                                        '& .MuiRating-iconFilled': {
                                                            color: '#ffd700'
                                                        },
                                                        '& .MuiRating-iconEmpty': {
                                                            color: 'rgba(255,255,255,0.5)'
                                                        }
                                                    }}
                                                />
                                                <Typography variant="body1" sx={{ mt: 1, color: 'rgba(255,255,255,0.95)', fontWeight: 500 }}>
                                                    Based on 2,847 reviews
                                                </Typography>
                                            </Box>
                                            <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                                            <Box sx={{ textAlign: 'left' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <Typography variant="body1" sx={{ minWidth: 30, color: 'white', fontWeight: 600 }}>5★</Typography>
                                                    <Box sx={{ 
                                                        mx: 2, 
                                                        width: 120, 
                                                        height: 10, 
                                                        borderRadius: 5,
                                                        bgcolor: 'rgba(255,255,255,0.2)',
                                                        overflow: 'hidden'
                                                    }}>
                                                        <Box sx={{
                                                            width: '75%',
                                                            height: '100%',
                                                            bgcolor: '#ffd700',
                                                            borderRadius: 5
                                                        }} />
                                                    </Box>
                                                    <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>75%</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <Typography variant="body1" sx={{ minWidth: 30, color: 'white', fontWeight: 600 }}>4★</Typography>
                                                    <Box sx={{ 
                                                        mx: 2, 
                                                        width: 120, 
                                                        height: 10, 
                                                        borderRadius: 5,
                                                        bgcolor: 'rgba(255,255,255,0.2)',
                                                        overflow: 'hidden'
                                                    }}>
                                                        <Box sx={{
                                                            width: '15%',
                                                            height: '100%',
                                                            bgcolor: '#ffd700',
                                                            borderRadius: 5
                                                        }} />
                                                    </Box>
                                                    <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>15%</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Typography variant="body1" sx={{ minWidth: 30, color: 'white', fontWeight: 600 }}>3★</Typography>
                                                    <Box sx={{ 
                                                        mx: 2, 
                                                        width: 120, 
                                                        height: 10, 
                                                        borderRadius: 5,
                                                        bgcolor: 'rgba(255,255,255,0.2)',
                                                        overflow: 'hidden'
                                                    }}>
                                                        <Box sx={{
                                                            width: '10%',
                                                            height: '100%',
                                                            bgcolor: '#ffd700',
                                                            borderRadius: 5
                                                        }} />
                                                    </Box>
                                                    <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>10%</Typography>
                                                </Box>
                                            </Box>
                                        </Stack>
                                    </Paper>

                                    {/* Featured Review Carousel */}
                                    <Box sx={{ position: 'relative', mb: 4 }}>
                                        <Typography variant="h5" fontWeight="bold" textAlign="center" sx={{ mb: 3 }}>
                                            Featured Reviews
                                        </Typography>
                                        
                                        {/* Navigation Dots */}
                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 3 }}>
                                            {testimonials.map((_, idx) => (
                                                <Box
                                                    key={idx}
                                                    onClick={() => setTestimonialIndex(idx)}
                                                    sx={{
                                                        width: testimonialIndex === idx ? 32 : 8,
                                                        height: 8,
                                                        borderRadius: 4,
                                                        bgcolor: testimonialIndex === idx ? 'primary.main' : 'grey.300',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': {
                                                            bgcolor: testimonialIndex === idx ? 'primary.dark' : 'grey.400'
                                                        }
                                                    }}
                                                />
                                            ))}
                                        </Box>

                                        {/* Review Cards */}
                                        <Box 
                                            sx={{ position: 'relative', minHeight: 280, mb: 3 }}
                                            onMouseEnter={() => setPauseTestimonials(true)}
                                            onMouseLeave={() => setPauseTestimonials(false)}
                                        >
                                            {testimonials.map((testimonial, idx) => (
                                                <Box
                                                    key={idx}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        opacity: testimonialIndex === idx ? 1 : 0,
                                                        transform: testimonialIndex === idx ? 'translateX(0)' : 'translateX(20px)',
                                                        transition: 'all 0.6s ease-in-out',
                                                        pointerEvents: testimonialIndex === idx ? 'auto' : 'none',
                                                    }}
                                                >
                                                    <Paper
                                                        elevation={3}
                                                        sx={{
                                                            p: 4,
                                                            bgcolor: 'background.paper',
                                                            borderRadius: 3,
                                                            border: '2px solid',
                                                            borderColor: 'primary.light',
                                                        }}
                                                    >
                                                        <Stack spacing={3}>
                                                            {/* Header */}
                                                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                                                <Stack direction="row" spacing={2} alignItems="center">
                                                                    <Avatar 
                                                                        sx={{ 
                                                                            bgcolor: 'primary.main',
                                                                            width: 56,
                                                                            height: 56,
                                                                            fontSize: 24
                                                                        }}
                                                                    >
                                                                        {testimonial.avatar}
                                                                    </Avatar>
                                                                    <Box>
                                                                        <Typography variant="h6" fontWeight="bold">
                                                                            {testimonial.name}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            {testimonial.role}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="success.main" fontWeight="600">
                                                                            ✓ Verified Purchase
                                                                        </Typography>
                                                                    </Box>
                                                                </Stack>
                                                                <Box sx={{ textAlign: 'right' }}>
                                                                    <Rating value={testimonial.rating} readOnly size="medium" />
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        2 weeks ago
                                                                    </Typography>
                                                                </Box>
                                                            </Stack>
                                                            
                                                            {/* Review Text */}
                                                            <Box>
                                                                <Typography 
                                                                    variant="body1" 
                                                                    sx={{ 
                                                                        fontStyle: 'italic',
                                                                        lineHeight: 1.8,
                                                                        color: 'text.primary',
                                                                        fontSize: '1.1rem'
                                                                    }}
                                                                >
                                                                    "{testimonial.text}"
                                                                </Typography>
                                                            </Box>
                                                            
                                                            {/* Footer */}
                                                            <Divider />
                                                            <Stack direction="row" spacing={2}>
                                                                <Chip 
                                                                    label="Helpful (42)" 
                                                                    variant="outlined" 
                                                                    size="small"
                                                                    onClick={() => {}}
                                                                    sx={{ cursor: 'pointer' }}
                                                                />
                                                                <Chip 
                                                                    label="Share" 
                                                                    variant="outlined" 
                                                                    size="small"
                                                                    onClick={() => {}}
                                                                    sx={{ cursor: 'pointer' }}
                                                                />
                                                            </Stack>
                                                        </Stack>
                                                    </Paper>
                                                </Box>
                                            ))}
                                        </Box>

                                        {/* Navigation Arrows */}
                                        <Stack direction="row" justifyContent="center" spacing={2}>
                                            <IconButton 
                                                onClick={() => setTestimonialIndex((prev) => 
                                                    prev === 0 ? testimonials.length - 1 : prev - 1
                                                )}
                                                sx={{ 
                                                    bgcolor: 'action.hover',
                                                    '&:hover': { bgcolor: 'action.selected' }
                                                }}
                                            >
                                                <ExpandLess sx={{ transform: 'rotate(-90deg)' }} />
                                            </IconButton>
                                            <IconButton 
                                                onClick={() => setTestimonialIndex((prev) => 
                                                    (prev + 1) % testimonials.length
                                                )}
                                                sx={{ 
                                                    bgcolor: 'action.hover',
                                                    '&:hover': { bgcolor: 'action.selected' }
                                                }}
                                            >
                                                <ExpandMore sx={{ transform: 'rotate(-90deg)' }} />
                                            </IconButton>
                                        </Stack>
                                    </Box>

                                    {/* All Reviews Grid */}
                                    <Divider sx={{ my: 4 }} />
                                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
                                        All Reviews
                                    </Typography>
                                    <Grid container spacing={3}>
                                        {testimonials.map((testimonial, idx) => (
                                            <Grid item xs={12} md={6} key={idx}>
                                                <Paper
                                                    elevation={1}
                                                    sx={{
                                                        p: 3,
                                                        height: '100%',
                                                        borderRadius: 2,
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': {
                                                            elevation: 4,
                                                            transform: 'translateY(-2px)'
                                                        }
                                                    }}
                                                >
                                                    <Stack spacing={2}>
                                                        <Stack direction="row" justifyContent="space-between">
                                                            <Box>
                                                                <Typography variant="subtitle1" fontWeight="bold">
                                                                    {testimonial.name}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {testimonial.role}
                                                                </Typography>
                                                            </Box>
                                                            <Rating value={testimonial.rating} readOnly size="small" />
                                                        </Stack>
                                                        <Typography variant="body2" color="text.secondary">
                                                            "{testimonial.text}"
                                                        </Typography>
                                                    </Stack>
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>

                            </TabPanel>
                        </Box>

                        {/* Sticky bottom bar for mobile */}
                        {isMobile && selectedOption && (
                            <Paper
                                elevation={8}
                                sx={{
                                    position: 'sticky',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    p: 2,
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'background.paper',
                                }}
                            >
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">
                                            Selected:
                                        </Typography>
                                        <Typography variant="h6" fontWeight="bold">
                                            ${selectedOption.price}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            startIcon={<ShoppingCart />}
                                            onClick={handleAddToCart}
                                            sx={{
                                                bgcolor: '#ff5e1c',
                                                '&:hover': { bgcolor: '#e54e0f' }
                                            }}
                                        >
                                            Add to Cart
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}
                    </DialogContent>
                </Box>
            </Dialog>

            {/* Cart Success Dialog */}
            <Dialog
                open={cartDialog}
                onClose={() => setCartDialog(false)}
                maxWidth="sm"
                fullWidth
                TransitionComponent={Slide}
                TransitionProps={{ direction: 'up' }}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'visible',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                    }
                }}
            >
                {/* Decorative top border */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 6,
                        background: 'linear-gradient(90deg, #ff5e1c 0%, #ff8c00 50%, #ff5e1c 100%)',
                        animation: `${shimmer} 2s infinite`,
                    }}
                />
                
                <Box sx={{ p: 4, textAlign: 'center', position: 'relative' }}>
                    {/* Success animation */}
                    <Box
                        sx={{
                            position: 'relative',
                            width: 100,
                            height: 100,
                            mx: 'auto',
                            mb: 3,
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #ff5e1c20 0%, #ff8c0010 100%)',
                                animation: `${pulse} 2s infinite`,
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 10,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #ff5e1c30 0%, #ff8c0020 100%)',
                                animation: `${pulse} 2s infinite 0.5s`,
                            }}
                        />
                        <Box
                            sx={{
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                                bgcolor: '#ff5e1c',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 8px 24px rgba(255, 94, 28, 0.3)',
                                animation: `${float} 3s ease-in-out infinite`,
                            }}
                        >
                            <CheckCircle 
                                sx={{ 
                                    fontSize: 56, 
                                    color: 'white',
                                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                                }} 
                            />
                        </Box>
                    </Box>
                    
                    <Typography 
                        variant="h4" 
                        fontWeight="bold" 
                        gutterBottom
                        sx={{
                            background: 'linear-gradient(45deg, #ff5e1c, #ff8c00)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 1,
                        }}
                    >
                        Successfully Added!
                    </Typography>
                    
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        {selectedOption?.displayName} is now in your cart
                    </Typography>
                    
                    {/* Product preview */}
                    <Paper
                        elevation={0}
                        sx={{ 
                            p: 2.5, 
                            mb: 3,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
                        }}
                    >
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={3}>
                                <Box
                                    sx={{
                                        position: 'relative',
                                        paddingTop: '75%',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        bgcolor: 'grey.100',
                                    }}
                                >
                                    <img
                                        src={product?.imageUrl}
                                        alt={product?.name}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={9}>
                                <Box sx={{ textAlign: 'left' }}>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        {product?.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {selectedOption?.displayName}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1 }}>
                                        <Typography variant="h6" fontWeight="bold" color="primary">
                                            ${selectedOption?.price}
                                        </Typography>
                                        {selectedOption?.isTaxable && (
                                            <Typography variant="caption" color="text.secondary">
                                                + tax
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                    
                    {/* Cart summary */}
                    <Box
                        sx={{
                            p: 2.5,
                            mb: 4,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #ff5e1c08 0%, #ff5e1c05 100%)',
                            border: '1px dashed',
                            borderColor: '#ff5e1c30',
                        }}
                    >
                        <Grid container spacing={3}>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Items in Cart
                                </Typography>
                                <Typography variant="h6" fontWeight="bold">
                                    {cart.length}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Subtotal
                                </Typography>
                                <Typography variant="h6" fontWeight="bold">
                                    ${cart.reduce((total, item) => total + (item.price * item.quantity), 0).toFixed(2)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                    
                    {/* Action buttons */}
                    <Stack spacing={2}>
                        <Button
                            variant="contained"
                            size="large"
                            fullWidth
                            onClick={() => {
                                setCartDialog(false);
                                onClose();
                                navigate('/checkout');
                            }}
                            endIcon={<ArrowForward />}
                            sx={{
                                py: 1.75,
                                bgcolor: '#ff5e1c',
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                boxShadow: '0 4px 12px rgba(255, 94, 28, 0.3)',
                                '&:hover': { 
                                    bgcolor: '#e54e0f',
                                    boxShadow: '0 6px 16px rgba(255, 94, 28, 0.4)',
                                    transform: 'translateY(-1px)',
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            Proceed to Checkout
                        </Button>
                        
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => {
                                    setCartDialog(false);
                                    onClose();
                                }}
                                sx={{ 
                                    py: 1.25,
                                    borderColor: 'grey.300',
                                    color: 'text.primary',
                                    '&:hover': {
                                        borderColor: 'grey.400',
                                        bgcolor: 'grey.50',
                                    }
                                }}
                            >
                                Continue Shopping
                            </Button>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => {
                                    setCartDialog(false);
                                    onClose();
                                    navigate('/cart');
                                }}
                                startIcon={<ShoppingCart />}
                                sx={{
                                    py: 1.25,
                                    borderColor: '#2196f3',
                                    color: '#2196f3',
                                    '&:hover': { 
                                        borderColor: '#1976d2',
                                        bgcolor: '#2196f308',
                                    }
                                }}
                            >
                                View Cart
                            </Button>
                        </Stack>
                    </Stack>
                    
                    {/* Trust badge */}
                    <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <Security sx={{ fontSize: 16, color: 'success.main' }} />
                        <Typography variant="caption" color="text.secondary">
                            Secure checkout • 100% money-back guarantee
                        </Typography>
                    </Box>
                </Box>
            </Dialog>

            {/* Share Snackbar */}
            <Snackbar
                open={shareSnackbar}
                autoHideDuration={3000}
                onClose={() => setShareSnackbar(false)}
                message="Link copied to clipboard!"
                action={
                    <IconButton
                        size="small"
                        color="inherit"
                        onClick={() => setShareSnackbar(false)}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                }
            />
        </>
    );
};

export default ProductDetailsEnhanced;